import { Component, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges, ElementRef, ViewChild, NgZone, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type DateSelectorMode = 'single' | 'range' | 'multiple';
export type DateSelectorSize = 'small' | 'medium' | 'large';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export type DateSelectorValue = Date | DateRange | Date[] | null;

@Component({
  selector: 'app-date-selector',
  templateUrl: './date-selector.component.html',
  styleUrls: ['./date-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateSelectorComponent),
      multi: true
    }
  ]
})
export class DateSelectorComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  @ViewChild('triggerEl') triggerEl!: ElementRef<HTMLElement>;
  @ViewChild('panelEl') panelEl!: ElementRef<HTMLElement>;

  @Input() label: string = '';
  @Input() placeholder: string = 'Select date';
  @Input() mode: DateSelectorMode = 'single';
  @Input() size: DateSelectorSize = 'medium';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage: string = '';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() showClearButton: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() value: DateSelectorValue = null;

  @Output() valueChange = new EventEmitter<DateSelectorValue>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  selectorId: string = `date-selector-${Math.random().toString(36).substr(2, 9)}`;
  isOpen: boolean = false;
  panelTop: number = 0;
  panelLeft: number = 0;
  viewYear: number = new Date().getFullYear();
  viewMonth: number = new Date().getMonth();
  weeks: Date[][] = [];
  hoveredDate: Date | null = null;

  weekDayLabels: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  monthNames: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  private onChange: (value: DateSelectorValue) => void = () => {};
  private onTouched: () => void = () => {};
  private boundDocumentClick: (event: MouseEvent) => void;

  constructor(private ngZone: NgZone, private elementRef: ElementRef) {
    this.boundDocumentClick = (event: MouseEvent) => this.onDocumentClick(event);
  }

  ngOnInit(): void {
    this.syncViewToValue();
    this.buildCalendar();
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousedown', this.boundDocumentClick, true);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.boundDocumentClick, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.value = this.sanitizeValue(this.value);
      this.syncViewToValue();
      this.buildCalendar();
    }
    if (changes['mode'] && !changes['mode'].firstChange) {
      this.value = null;
      this.emitChange();
    }
  }

  writeValue(value: DateSelectorValue): void {
    this.value = this.sanitizeValue(value);
    this.syncViewToValue();
    this.buildCalendar();
  }

  registerOnChange(fn: (value: DateSelectorValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }
    this.isOpen = true;
    this.syncViewToValue();
    this.buildCalendar();
    this.opened.emit();
    setTimeout(() => this.positionPanel(), 0);
  }

  private positionPanel(): void {
    if (!this.triggerEl) {
      return;
    }
    const rect = this.triggerEl.nativeElement.getBoundingClientRect();
    const panelWidth = 296;
    const panelHeight = 340;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = rect.bottom + 6;
    let left = rect.left;

    if (left + panelWidth + margin > vw) {
      left = Math.max(margin, vw - panelWidth - margin);
    }
    if (top + panelHeight + margin > vh) {
      top = Math.max(margin, rect.top - panelHeight - 6);
    }

    this.panelTop = top;
    this.panelLeft = left;
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.hoveredDate = null;
    this.onTouched();
    this.closed.emit();
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  prevMonth(event: Event): void {
    event.stopPropagation();
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear = this.viewYear - 1;
    } else {
      this.viewMonth = this.viewMonth - 1;
    }
    this.buildCalendar();
  }

  nextMonth(event: Event): void {
    event.stopPropagation();
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear = this.viewYear + 1;
    } else {
      this.viewMonth = this.viewMonth + 1;
    }
    this.buildCalendar();
  }

  prevYear(event: Event): void {
    event.stopPropagation();
    this.viewYear = this.viewYear - 1;
    this.buildCalendar();
  }

  nextYear(event: Event): void {
    event.stopPropagation();
    this.viewYear = this.viewYear + 1;
    this.buildCalendar();
  }

  goToToday(event: Event): void {
    event.stopPropagation();
    const now = new Date();
    this.viewMonth = now.getMonth();
    this.viewYear = now.getFullYear();
    this.buildCalendar();
  }

  onDateClick(date: Date, event: Event): void {
    event.stopPropagation();
    if (this.isDateDisabled(date)) {
      return;
    }
    if (this.mode === 'single') {
      this.value = this.cloneDate(date);
      this.emitChange();
      if (this.closeOnSelect) {
        this.close();
      }
      return;
    }
    if (this.mode === 'multiple') {
      const list = this.getMultipleArray();
      const idx = list.findIndex(d => this.isSameDay(d, date));
      if (idx === -1) {
        list.push(this.cloneDate(date));
      } else {
        list.splice(idx, 1);
      }
      list.sort((a, b) => a.getTime() - b.getTime());
      this.value = list;
      this.emitChange();
      return;
    }
    if (this.mode === 'range') {
      const range = this.getRange();
      const hasStart = range.start !== null;
      const hasEnd = range.end !== null;
      if (!hasStart || (hasStart && hasEnd)) {
        this.value = { start: this.cloneDate(date), end: null };
        this.emitChange();
        return;
      }
      let start = range.start as Date;
      let end = this.cloneDate(date);
      if (end.getTime() < start.getTime()) {
        const tmp = start;
        start = end;
        end = tmp;
      }
      this.value = { start: start, end: end };
      this.emitChange();
      if (this.closeOnSelect) {
        this.close();
      }
    }
  }

  onDateHover(date: Date): void {
    if (this.mode === 'range') {
      this.hoveredDate = date;
    }
  }

  onDateLeave(): void {
    this.hoveredDate = null;
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.value = null;
    this.emitChange();
  }

  buildCalendar(): void {
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startDay = firstOfMonth.getDay();
    const gridStart = new Date(this.viewYear, this.viewMonth, 1 - startDay);
    const weeks: Date[][] = [];
    for (let w = 0; w < 6; w++) {
      const week: Date[] = [];
      for (let d = 0; d < 7; d++) {
        const day = new Date(gridStart);
        day.setDate(gridStart.getDate() + w * 7 + d);
        week.push(day);
      }
      weeks.push(week);
    }
    this.weeks = weeks;
  }

  isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.viewMonth && date.getFullYear() === this.viewYear;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return this.isSameDay(date, today);
  }

  isDateDisabled(date: Date): boolean {
    if (this.minDate && date.getTime() < this.startOfDay(this.minDate).getTime()) {
      return true;
    }
    if (this.maxDate && date.getTime() > this.endOfDay(this.maxDate).getTime()) {
      return true;
    }
    return false;
  }

  isSelected(date: Date): boolean {
    if (this.value === null) {
      return false;
    }
    if (this.mode === 'single') {
      return this.isSameDay(date, this.value as Date);
    }
    if (this.mode === 'multiple') {
      const list = this.value as Date[];
      return list.some(d => this.isSameDay(d, date));
    }
    if (this.mode === 'range') {
      const range = this.value as DateRange;
      if (range.start && this.isSameDay(date, range.start)) {
        return true;
      }
      if (range.end && this.isSameDay(date, range.end)) {
        return true;
      }
    }
    return false;
  }

  isRangeStart(date: Date): boolean {
    if (this.mode !== 'range' || this.value === null) {
      return false;
    }
    const range = this.value as DateRange;
    if (range.start === null) {
      return false;
    }
    return this.isSameDay(date, range.start);
  }

  isRangeEnd(date: Date): boolean {
    if (this.mode !== 'range' || this.value === null) {
      return false;
    }
    const range = this.value as DateRange;
    if (range.end === null) {
      return false;
    }
    return this.isSameDay(date, range.end);
  }

  isInRange(date: Date): boolean {
    if (this.mode !== 'range' || this.value === null) {
      return false;
    }
    const range = this.value as DateRange;
    if (range.start === null) {
      return false;
    }
    const startTime = this.startOfDay(range.start).getTime();
    const dateTime = this.startOfDay(date).getTime();
    if (range.end !== null) {
      const endTime = this.startOfDay(range.end).getTime();
      return dateTime >= startTime && dateTime <= endTime;
    }
    if (this.hoveredDate !== null) {
      const hoverTime = this.startOfDay(this.hoveredDate).getTime();
      const lo = Math.min(startTime, hoverTime);
      const hi = Math.max(startTime, hoverTime);
      return dateTime >= lo && dateTime <= hi;
    }
    return false;
  }

  getDisplayValue(): string {
    if (this.value === null) {
      return '';
    }
    if (this.mode === 'single') {
      return this.formatDate(this.value as Date);
    }
    if (this.mode === 'multiple') {
      const list = this.value as Date[];
      if (list.length === 0) {
        return '';
      }
      if (list.length === 1) {
        return this.formatDate(list[0]);
      }
      return `${this.formatDate(list[0])} +${list.length - 1} more`;
    }
    if (this.mode === 'range') {
      const range = this.value as DateRange;
      if (range.start === null) {
        return '';
      }
      const startStr = this.formatDate(range.start);
      if (range.end === null) {
        return `${startStr} – ...`;
      }
      return `${startStr} – ${this.formatDate(range.end)}`;
    }
    return '';
  }

  hasValue(): boolean {
    if (this.value === null) {
      return false;
    }
    if (this.mode === 'multiple') {
      return (this.value as Date[]).length > 0;
    }
    if (this.mode === 'range') {
      const range = this.value as DateRange;
      return range.start !== null;
    }
    return true;
  }

  getRootClasses(): string {
    const classes = ['date-selector'];
    classes.push(`size-${this.size}`);
    if (this.disabled) {
      classes.push('disabled');
    }
    if (this.error) {
      classes.push('error');
    }
    if (this.isOpen) {
      classes.push('open');
    }
    if (this.required) {
      classes.push('required');
    }
    return classes.join(' ');
  }

  getDayClasses(date: Date): string {
    const classes = ['day-cell'];
    if (!this.isCurrentMonth(date)) {
      classes.push('outside');
    }
    if (this.isToday(date)) {
      classes.push('today');
    }
    if (this.isDateDisabled(date)) {
      classes.push('disabled');
    }
    if (this.isSelected(date)) {
      classes.push('selected');
    }
    if (this.isRangeStart(date)) {
      classes.push('range-start');
    }
    if (this.isRangeEnd(date)) {
      classes.push('range-end');
    }
    if (this.isInRange(date) && !this.isSelected(date)) {
      classes.push('in-range');
    }
    return classes.join(' ');
  }

  private syncViewToValue(): void {
    let target: Date | null = null;
    if (this.value !== null) {
      if (this.mode === 'single' && this.value instanceof Date) {
        target = this.value;
      } else if (this.mode === 'multiple' && Array.isArray(this.value) && this.value.length > 0) {
        target = this.value[0];
      } else if (this.mode === 'range') {
        const range = this.value as DateRange;
        if (range.start) {
          target = range.start;
        }
      }
    }
    if (target !== null) {
      this.viewYear = target.getFullYear();
      this.viewMonth = target.getMonth();
    }
  }

  private getMultipleArray(): Date[] {
    if (Array.isArray(this.value)) {
      return (this.value as Date[]).map(d => this.cloneDate(d));
    }
    return [];
  }

  private getRange(): DateRange {
    if (this.value !== null && !Array.isArray(this.value) && !(this.value instanceof Date)) {
      const r = this.value as DateRange;
      return { start: r.start ? this.cloneDate(r.start) : null, end: r.end ? this.cloneDate(r.end) : null };
    }
    return { start: null, end: null };
  }

  private emitChange(): void {
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  private onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.ngZone.run(() => this.close());
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }

  private endOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  }

  private sanitizeValue(value: any): DateSelectorValue {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (this.mode === 'single') {
      const d = this.coerceDate(value);
      return d;
    }
    if (this.mode === 'multiple') {
      if (!Array.isArray(value)) {
        return null;
      }
      const list: Date[] = [];
      for (const item of value) {
        const d = this.coerceDate(item);
        if (d) {
          list.push(d);
        }
      }
      return list;
    }
    if (this.mode === 'range') {
      if (typeof value !== 'object' || Array.isArray(value)) {
        return null;
      }
      const r = value as { start: any; end: any };
      return {
        start: this.coerceDate(r.start),
        end: this.coerceDate(r.end)
      };
    }
    return null;
  }

  private coerceDate(input: any): Date | null {
    if (input === null || input === undefined || input === '') {
      return null;
    }
    if (input instanceof Date) {
      if (isNaN(input.getTime())) {
        return null;
      }
      return input;
    }
    if (typeof input === 'string' || typeof input === 'number') {
      const d = new Date(input);
      if (isNaN(d.getTime())) {
        return null;
      }
      return d;
    }
    if (typeof input.toDate === 'function') {
      const d = input.toDate();
      if (d instanceof Date && !isNaN(d.getTime())) {
        return d;
      }
    }
    return null;
  }

  private cloneDate(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private formatDate(d: Date): string {
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    const yyyy = d.getFullYear().toString();
    return `${mm}/${dd}/${yyyy}`;
  }
}
